import { Link, useLocation } from "react-router-dom";
import { Layers, Plus, Trash2, LogOut, X, Images, Columns2, Clapperboard, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useVideoJobs } from "@/context/VideoJobsContext";
import { PROVIDER_META, findModel } from "@/lib/meta";

export const Sidebar = ({ conversations, activeId, catalog, open, onClose, onDelete }) => {
  const { user, logout } = useAuth();
  const { unseen, jobs } = useVideoJobs();
  const rendering = (jobs || []).filter((j) => j.status === "queued" || j.status === "running").length;
  const { pathname } = useLocation();
  const navCls = (active) => `flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${active ? "bg-indigo-500/10 text-indigo-200 border border-indigo-500/30" : "text-slate-400 hover:text-white hover:bg-[#181B2A] border border-transparent"}`;

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={onClose} />}
      <aside data-testid="sidebar" className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-[#0D0F18] border-r border-slate-800/80 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center justify-between px-5 h-16 border-b border-slate-800/80">
          <Link to="/" className="flex items-center gap-2.5" data-testid="sidebar-logo">
            <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center"><Layers className="w-4 h-4 text-white" /></div>
            <span className="font-display font-semibold text-slate-100">OmniModel</span>
          </Link>
          <button onClick={onClose} className="lg:hidden text-slate-400 hover:text-white" data-testid="sidebar-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-3">
          <Link to="/" onClick={onClose} data-testid="new-inquiry-btn" className="flex items-center justify-center gap-2 w-full bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
            <Plus className="w-4 h-4" /> New inquiry
          </Link>
          <div className="grid grid-cols-3 gap-2">
            <Link to="/compare" onClick={onClose} data-testid="nav-compare" className={navCls(pathname === "/compare")}><Columns2 className="w-4 h-4 shrink-0" /> Compare</Link>
            <Link to="/studio" onClick={onClose} data-testid="nav-studio" className={`relative ${navCls(pathname === "/studio")}`}>
              <Clapperboard className="w-4 h-4 shrink-0" /> Studio
              {unseen > 0 && <span data-testid="studio-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center">{unseen}</span>}
              {unseen === 0 && rendering > 0 && <span data-testid="studio-rendering-dot" className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-indigo-400 animate-pulse" title={`${rendering} rendering`} />}
            </Link>
            <Link to="/gallery" onClick={onClose} data-testid="nav-gallery" className={navCls(pathname === "/gallery")}><Images className="w-4 h-4 shrink-0" /> Gallery</Link>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5" data-testid="conversation-list">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 px-2 pt-1 pb-2">Threads</p>
          {conversations.length === 0 && <p className="text-xs text-slate-500 px-2 py-4" data-testid="no-conversations">No threads yet. Start an inquiry.</p>}
          {conversations.map((c) => {
            const meta = PROVIDER_META[c.provider];
            const model = findModel(catalog, c.provider, c.model);
            const active = c.id === activeId;
            return (
              <div key={c.id} className={`group relative rounded-lg transition-colors ${active ? "bg-indigo-500/10 border border-indigo-500/30" : "hover:bg-[#181B2A] border border-transparent"}`}>
                <Link to={`/c/${c.id}`} onClick={onClose} data-testid={`thread-item-${c.id}`} className="block px-3 py-2.5 pr-9">
                  <p className={`text-sm truncate ${active ? "text-slate-100" : "text-slate-300"}`}>{c.title}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 truncate">
                    <span className={`w-1.5 h-1.5 rounded-full ${meta?.dot || "bg-slate-500"}`} />
                    {model?.name || c.model}
                  </p>
                </Link>
                <button onClick={() => onDelete(c.id)} data-testid={`thread-delete-${c.id}`} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Delete thread">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-slate-800/80 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#1F2338] flex items-center justify-center text-xs font-semibold text-indigo-300 uppercase">{user?.name?.[0] || "U"}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-200 truncate" data-testid="user-name">{user?.name}</p>
            <p className="text-[11px] text-slate-500 truncate" data-testid="user-email">{user?.email}</p>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#1F2338] transition-colors" aria-label="Log out"><LogOut className="w-4 h-4" /></button>
        </div>
        {user?.role === "admin" && (
          <Link to="/admin" onClick={onClose} data-testid="nav-admin" className={`mx-4 mb-4 -mt-1 ${navCls(pathname === "/admin")}`}><ShieldCheck className="w-4 h-4 shrink-0" /> Admin · Users</Link>
        )}
      </aside>
    </>
  );
};
